/**
 * features/hr/ClockingStartTimes.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — when each type of staff starts work, for the clocking
 * "late" rule. Teaching, management, administration, support and
 * ancillary staff each get a weekday start and a Saturday start; a
 * Saturday left empty means that type does not work Saturdays, so nobody
 * in it is late on one. A type left at the school times follows
 * SCHOOL.clocking.
 *
 * Saved to hrClockingSettings/school (rules: HR staff write). Because
 * every clocking screen works lateness out on sight, a change here
 * corrects every day already uploaded.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import { doc } from 'firebase/firestore';
import { db, setDoc } from '@/lib/firebase';
import { modalBackdrop, modalSurface, btnPrimary, btnGhost, glassFieldClass } from '@/features/finance/ui';
import { useAuth } from '@/contexts/AuthContext';
import { recordHrAudit } from '@/lib/hrAudit';
import {
  SCHOOL_CLOCKING, CLOCKING_SETTINGS_COLLECTION, CLOCKING_SETTINGS_DOC, type CategoryStarts,
} from '@/lib/clockingRules';
import { STAFF_CATEGORY_LABELS, type StaffCategory } from '@/lib/hr';

const CATEGORIES: StaffCategory[] = ['teaching', 'management', 'admin', 'support', 'ancillary'];

interface Draft { weekday: string; saturday: string; noSaturday: boolean }

export default function ClockingStartTimes({
  open, onClose, starts,
}: { open: boolean; onClose: () => void; starts: CategoryStarts }) {
  const { userData } = useAuth();
  const [draft, setDraft] = useState<Record<StaffCategory, Draft> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const d = {} as Record<StaffCategory, Draft>;
    for (const c of CATEGORIES) {
      const own = starts[c];
      const sat = own?.saturdayStart === undefined ? SCHOOL_CLOCKING.saturdayStart : own.saturdayStart;
      d[c] = { weekday: own?.weekdayStart ?? SCHOOL_CLOCKING.weekdayStart, saturday: sat ?? '', noSaturday: sat === null };
    }
    setDraft(d);
    setError('');
  }, [open, starts]);

  if (!open || !draft) return null;

  async function save() {
    if (!draft) return;
    for (const c of CATEGORIES) {
      if (!draft[c].weekday) { setError(`${STAFF_CATEGORY_LABELS[c]} needs a weekday start.`); return; }
      if (!draft[c].noSaturday && !draft[c].saturday) { setError(`${STAFF_CATEGORY_LABELS[c]}: set a Saturday start, or tick "No Saturdays".`); return; }
    }
    setBusy(true);
    setError('');
    try {
      const out: CategoryStarts = {};
      for (const c of CATEGORIES) {
        out[c] = { weekdayStart: draft[c].weekday, saturdayStart: draft[c].noSaturday ? null : draft[c].saturday };
      }
      const at = new Date().toISOString();
      await setDoc(doc(db, CLOCKING_SETTINGS_COLLECTION, CLOCKING_SETTINGS_DOC), { starts: out, updatedAt: at, updatedBy: userData?.uid ?? 'unknown' });
      await recordHrAudit({
        action: 'clocking.startTimes',
        actor: { id: userData?.uid ?? 'unknown', email: userData?.email ?? null },
        before: { starts },
        after: { starts: out },
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const set = (c: StaffCategory, patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, [c]: { ...d[c], ...patch } } : d));

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('hr-scope w-full max-w-xl')} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="cst-title">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2 id="cst-title" className="text-lg font-bold text-gray-900 dark:text-white">Start times by staff type</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">A first punch after the start is late. Applies to every day already uploaded.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-2 font-semibold">Staff type</th>
                <th className="py-2 font-semibold">Weekdays</th>
                <th className="py-2 font-semibold">Saturdays</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {CATEGORIES.map((c) => (
                <tr key={c}>
                  <td className="py-2 pr-3 font-medium text-gray-800 dark:text-gray-100">{STAFF_CATEGORY_LABELS[c]}</td>
                  <td className="py-2 pr-3">
                    <input type="time" value={draft[c].weekday} onChange={(e) => set(c, { weekday: e.target.value })} className={glassFieldClass('py-1 w-28')} aria-label={`${STAFF_CATEGORY_LABELS[c]} weekday start`} />
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <input
                        type="time" value={draft[c].saturday} disabled={draft[c].noSaturday}
                        onChange={(e) => set(c, { saturday: e.target.value })}
                        className={glassFieldClass('py-1 w-28 disabled:opacity-40')} aria-label={`${STAFF_CATEGORY_LABELS[c]} Saturday start`}
                      />
                      <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                        <input type="checkbox" checked={draft[c].noSaturday} onChange={(e) => set(c, { noSaturday: e.target.checked })} className="accent-[var(--accent-strong)]" />
                        No Saturdays
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-3">Public holidays and the school holidays have no start for anyone.</p>
          {error && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={save} disabled={busy}><Save size={15} /> Save start times</button>
        </div>
      </div>
    </div>
  );
}
